import React, { useEffect, useState } from 'react';
import { Wine, RefreshCw, Loader2, AlertCircle, Package, Gavel, Tag, BarChart3, Link2, AlertTriangle, ExternalLink } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

interface MasterRef {
    masterProductVintageId: string;
    masterProductId: string;
    masterProductName: string;
    producer: string;
    region: string;
    vintage: number;
    sourceUrl: string | null;
}

interface CellarItem {
    itemId: string;
    sku: string;
    parentSku: string;
    name: string;
    producer: string;
    vintage: number;
    colour: string;
    region: string;
    subregion: string;
    country: string;
    caseSize: number;
    bottleVolume: string;
    listedOnBbx: boolean;
    hasBids: boolean;
    storedStatus: string;
    storageLocation: string;
    purchasePrice: number;
    currentValue: number;
    bbxListingPrice: number;
    bidPrice: number;
    bidCount: number;
}

interface MasterSkuRef {
    masterSkuId: string;
    canonicalSkuCode: string;
    bottleSizeMl: number;
    caseSize: number;
    adjustedMarketPrice: number | null;
    wineSearcherPrice: number | null;
    leastListingPrice: number | null;
}

interface EnrichedCellarItem {
    item: CellarItem;
    masterRef: MasterRef | null;
    masterSkuRef: MasterSkuRef | null;
}

interface BidItem {
    bidId: string;
    sku: string;
    parentSku: string;
    name: string;
    producer: string;
    vintage: number;
    colour: string;
    region: string;
    proposedPrice: number;
    casesQty: number;
    caseSize: number;
    bottleVolume: string;
    bidStatus: string;
    createdAt: string;
}

interface EnrichedBid {
    bid: BidItem;
    masterRef: MasterRef | null;
    masterSkuRef: MasterSkuRef | null;
}

interface AccountSummary {
    totalCellarItems: number;
    listedOnBbx: number;
    withBidsReceived: number;
    bidsPlaced: number;
    matchedToMaster: number;
    unmatchedItems: number;
}

interface AuthStatus {
    sessionActive: boolean;
    keyExpiry: string;
}

type TabKey = 'all' | 'bbx' | 'bidsReceived' | 'bidsPlaced';

const BbrAccount: React.FC = () => {
    const [cellar, setCellar] = useState<EnrichedCellarItem[]>([]);
    const [bbxListings, setBbxListings] = useState<EnrichedCellarItem[]>([]);
    const [bidsReceived, setBidsReceived] = useState<EnrichedCellarItem[]>([]);
    const [bidsPlaced, setBidsPlaced] = useState<EnrichedBid[]>([]);
    const [summary, setSummary] = useState<AccountSummary | null>(null);
    const [authStatus, setAuthStatus] = useState<AuthStatus | null>(null);
    const [skuStatus, setSkuStatus] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<TabKey>('all');

    const fetchAll = async () => {
        try {
            const [cellarRes, bbxRes, bidsRecRes, bidsPlRes, summaryRes, authRes, skuStatusRes] = await Promise.all([
                fetch(`${API_BASE_URL}/api/bbr-account/cellar`),
                fetch(`${API_BASE_URL}/api/bbr-account/cellar/bbx-listings`),
                fetch(`${API_BASE_URL}/api/bbr-account/cellar/bids-received`),
                fetch(`${API_BASE_URL}/api/bbr-account/bids-placed`),
                fetch(`${API_BASE_URL}/api/bbr-account/summary`),
                fetch(`${API_BASE_URL}/api/bbr-account/auth-status`),
                fetch(`${API_BASE_URL}/api/bbr-account/sku-status`),
            ]);

            if (!cellarRes.ok) throw new Error('Failed to fetch cellar data');

            const [cellarData, bbxData, bidsRecData, bidsPlData, summaryData, authData, skuStatusData] = await Promise.all([
                cellarRes.json(),
                bbxRes.json(),
                bidsRecRes.json(),
                bidsPlRes.json(),
                summaryRes.json(),
                authRes.json(),
                skuStatusRes.ok ? skuStatusRes.json() : {},
            ]);

            setCellar(cellarData);
            setBbxListings(bbxData);
            setBidsReceived(bidsRecData);
            setBidsPlaced(bidsPlData);
            setSummary(summaryData);
            setAuthStatus(authData);
            setSkuStatus(skuStatusData || {});
            setError(null);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAll();
    }, []);

    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            await fetch(`${API_BASE_URL}/api/bbr-account/refresh`, { method: 'POST' });
            await fetchAll();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setRefreshing(false);
        }
    };

    const formatPrice = (price: number | null | undefined) => {
        if (price == null) return '—';
        return `£${price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // % difference of `value` relative to `benchmark` (e.g. listing/bid vs market). Null if either missing.
    const pctDiff = (value: number | null | undefined, benchmark: number | null | undefined): number | null => {
        if (value == null || benchmark == null || benchmark === 0) return null;
        return ((value - benchmark) / benchmark) * 100;
    };

    // A coloured "X vs Benchmark: ±N%" chip.
    //   goodWhen 'above'  → green when value is ABOVE benchmark (selling: listing/bid higher than market/WS is good).
    //   goodWhen 'below'  → green when value is BELOW benchmark (buying: your bid under market/WS is a good buy; above = dangerous/red).
    const diffChip = (
        label: string,
        value: number | null | undefined,
        benchmark: number | null | undefined,
        goodWhen: 'above' | 'below',
    ) => {
        const pct = pctDiff(value, benchmark);
        if (pct == null) return null;
        const isGood = goodWhen === 'above' ? pct >= 0 : pct <= 0;
        const bg = isGood ? 'var(--success-bg)' : 'var(--danger-bg)';
        const color = isGood ? 'var(--success)' : 'var(--danger)';
        return (
            <span key={label} style={styles.pill(bg, color)}>
                {label}: {pct >= 0 ? '+' : ''}{pct.toFixed(1)}%
            </span>
        );
    };

    const formatExpiry = (expiry: string) => {
        if (!expiry || expiry === 'none') return 'No key';
        const date = new Date(expiry);
        const now = new Date();
        const diffMs = date.getTime() - now.getTime();
        if (diffMs <= 0) return 'Expired';
        const mins = Math.floor(diffMs / 60000);
        const hrs = Math.floor(mins / 60);
        return hrs > 0 ? `${hrs}h ${mins % 60}m` : `${mins}m`;
    };

    const styles = {
        container: {
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '24px',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '24px',
        },
        title: {
            fontSize: '24px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        headerRight: {
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        authDot: (active: boolean) => ({
            display: 'inline-block',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: active ? 'var(--success)' : 'var(--danger)',
            marginRight: '6px',
        }),
        authInfo: {
            fontSize: '13px',
            color: 'var(--ink-500)',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
        },
        refreshButton: {
            padding: '8px 16px',
            backgroundColor: 'white',
            color: 'var(--ink-500)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500' as const,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        },
        summaryGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
        },
        statCard: (bg: string, color: string) => ({
            backgroundColor: bg,
            borderRadius: '12px',
            padding: '16px',
            border: '1px solid var(--border)',
        }),
        statValue: (color: string) => ({
            fontSize: '28px',
            fontWeight: '700' as const,
            color: color,
        }),
        statLabel: {
            fontSize: '13px',
            color: 'var(--ink-500)',
            marginTop: '4px',
        },
        tabBar: {
            display: 'flex',
            gap: '4px',
            marginBottom: '24px',
            borderBottom: '2px solid var(--border)',
        },
        tab: (active: boolean) => ({
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: active ? 'var(--claret-600)' : 'var(--ink-500)',
            border: 'none',
            borderBottom: active ? '2px solid var(--claret-600)' : '2px solid transparent',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: active ? '600' as const : '500' as const,
            marginBottom: '-2px',
        }),
        card: {
            backgroundColor: 'white',
            border: '1px solid var(--border)',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '12px',
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
        wineName: {
            fontSize: '16px',
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
            marginBottom: '4px',
        },
        wineDetails: {
            fontSize: '13px',
            color: 'var(--ink-500)',
        },
        pillRow: {
            display: 'flex',
            gap: '8px',
            marginTop: '8px',
            flexWrap: 'wrap' as const,
        },
        pill: (bg: string, color: string) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '3px 10px',
            backgroundColor: bg,
            color: color,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600' as const,
        }),
        priceRow: {
            display: 'flex',
            gap: '24px',
            marginTop: '10px',
            fontSize: '13px',
        },
        priceLabel: {
            color: 'var(--ink-500)',
        },
        priceValue: {
            fontWeight: '600' as const,
            color: 'var(--ink-900)',
        },
        matchLink: {
            fontSize: '12px',
            color: 'var(--claret-600)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
        },
        unmatchedLabel: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '12px',
            color: 'var(--warning)',
        },
    };

    if (loading) {
        return (
            <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Loader2 size={32} className="spinner" />
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...styles.container, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <div style={{ textAlign: 'center' }}>
                    <AlertCircle size={48} color="var(--danger)" />
                    <p style={{ marginTop: '16px', color: 'var(--danger)' }}>{error}</p>
                    <button style={{ ...styles.refreshButton, marginTop: '16px' }} onClick={handleRefresh}>
                        <RefreshCw size={16} /> Retry
                    </button>
                </div>
            </div>
        );
    }

    const tabData: Record<TabKey, { label: string; count: number }> = {
        all: { label: 'All Cellar', count: summary?.totalCellarItems || 0 },
        bbx: { label: 'BBX Listings', count: summary?.listedOnBbx || 0 },
        bidsReceived: { label: 'Bids Received', count: summary?.withBidsReceived || 0 },
        bidsPlaced: { label: 'Bids Placed', count: summary?.bidsPlaced || 0 },
    };

    const renderCellarCard = (enriched: EnrichedCellarItem) => {
        const { item, masterRef, masterSkuRef } = enriched;
        const ss = masterSkuRef ? skuStatus[masterSkuRef.masterSkuId] : null;
        // Use cellar service prices (rich data) when available, fall back to Algolia
        const purchasePrice = ss?.purchasePrice ?? item.purchasePrice;
        const listingPrice = ss?.bbxListingPrice ?? item.bbxListingPrice;
        const highestBid = ss?.highestBidReceived ?? item.bidPrice;
        // Market: prefer live cellar-services value, fall back to persisted master valuation, then Algolia.
        const marketPrice = ss?.cellarMarketPrice ?? masterSkuRef?.adjustedMarketPrice ?? item.currentValue;
        // Wine Searcher lowest only exists on the persisted master SKU.
        const wsPrice = masterSkuRef?.wineSearcherPrice ?? null;
        const changeValue = ss?.changeValue;
        const changeValuePct = ss?.changeValuePercentage;
        // Seller perspective: listing/top-bid ABOVE market/WS is good (green).
        const compareChips = [
            diffChip('Listing vs Market', listingPrice, marketPrice, 'above'),
            diffChip('Listing vs WS', listingPrice, wsPrice, 'above'),
            diffChip('Top Bid vs Market', highestBid, marketPrice, 'above'),
            diffChip('Top Bid vs WS', highestBid, wsPrice, 'above'),
        ].filter(Boolean);

        return (
            <div key={item.itemId || item.sku} style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={{ flex: 1 }}>
                        <div style={styles.wineName}>{item.name}</div>
                        <div style={styles.wineDetails}>
                            {item.producer} {item.region && `• ${item.region}`} {item.vintage ? `• ${item.vintage}` : ''}
                            {item.bottleVolume && ` • ${item.bottleVolume}`}
                            {item.caseSize && ` × ${item.caseSize}`}
                        </div>
                        <div style={styles.pillRow}>
                            {ss?.totalCases != null && (
                                <span style={styles.pill('var(--info-bg)', 'var(--info)')}>
                                    {ss.totalCases} {ss.totalCases === 1 ? 'case' : 'cases'}
                                </span>
                            )}
                            {item.storedStatus && (
                                <span style={styles.pill('var(--info-bg)', 'var(--info)')}>{item.storedStatus}</span>
                            )}
                            {item.listedOnBbx && (
                                <span style={styles.pill('var(--success-bg)', 'var(--success)')}>
                                    <Tag size={12} /> Listed
                                </span>
                            )}
                            {item.hasBids && (
                                <span style={styles.pill('var(--warning-bg)', '#854d0e')}>
                                    <Gavel size={12} /> Has Bids
                                </span>
                            )}
                            {changeValue != null && (
                                <span style={styles.pill(
                                    Number(changeValue) >= 0 ? 'var(--success-bg)' : 'var(--danger-bg)',
                                    Number(changeValue) >= 0 ? 'var(--success)' : 'var(--danger)'
                                )}>
                                    {Number(changeValue) >= 0 ? '+' : ''}£{Number(changeValue).toFixed(0)}
                                    {changeValuePct != null && ` (${Number(changeValuePct) >= 0 ? '+' : ''}${Number(changeValuePct).toFixed(1)}%)`}
                                </span>
                            )}
                        </div>
                        <div style={styles.priceRow}>
                            {purchasePrice != null && (
                                <span><span style={styles.priceLabel}>Purchase: </span><span style={styles.priceValue}>{formatPrice(purchasePrice)}</span></span>
                            )}
                            {listingPrice != null && (
                                <span><span style={styles.priceLabel}>My Listing: </span><span style={styles.priceValue}>{formatPrice(listingPrice)}</span></span>
                            )}
                            {highestBid != null && (
                                <span><span style={styles.priceLabel}>Highest Bid: </span><span style={styles.priceValue}>{formatPrice(highestBid)}</span></span>
                            )}
                            {marketPrice != null && (
                                <span><span style={styles.priceLabel}>Market: </span><span style={styles.priceValue}>{formatPrice(marketPrice)}</span></span>
                            )}
                            {wsPrice != null && (
                                <span><span style={styles.priceLabel}>WS Low: </span><span style={styles.priceValue}>{formatPrice(wsPrice)}</span></span>
                            )}
                        </div>
                        {compareChips.length > 0 && (
                            <div style={styles.pillRow}>{compareChips}</div>
                        )}
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                        {masterRef ? (
                            <>
                                <a href={`/master-products?vintageId=${encodeURIComponent(masterRef.masterProductVintageId)}`} style={styles.matchLink}>
                                    <Link2 size={12} /> {masterRef.masterProductName} ({masterRef.vintage})
                                </a>
                                {masterRef.sourceUrl && (
                                    <a href={masterRef.sourceUrl} target="_blank" rel="noopener noreferrer" style={styles.matchLink}>
                                        <ExternalLink size={12} /> View on BBR
                                    </a>
                                )}
                                {masterSkuRef && (
                                    <span style={{ fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'monospace' }}>
                                        SKU: {masterSkuRef.canonicalSkuCode}
                                    </span>
                                )}
                                {!masterSkuRef && (
                                    <span style={{ fontSize: '11px', color: 'var(--warning)' }}>
                                        Vintage match only
                                    </span>
                                )}
                            </>
                        ) : (
                            <span style={styles.unmatchedLabel}>
                                <AlertTriangle size={12} /> Unmatched
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderBidCard = (enriched: EnrichedBid) => {
        const { bid, masterRef, masterSkuRef } = enriched;
        const caseSize = bid.caseSize ?? masterSkuRef?.caseSize ?? null;
        const marketPrice = masterSkuRef?.adjustedMarketPrice ?? null;
        const wsPrice = masterSkuRef?.wineSearcherPrice ?? null;
        // Buyer perspective: a bid BELOW market/WS is a good buy (green); ABOVE is dangerous (red).
        const compareChips = [
            diffChip('Bid vs Market', bid.proposedPrice, marketPrice, 'below'),
            diffChip('Bid vs WS', bid.proposedPrice, wsPrice, 'below'),
        ].filter(Boolean);
        return (
            <div key={bid.bidId || bid.sku} style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={{ flex: 1 }}>
                        <div style={styles.wineName}>{bid.name}</div>
                        <div style={styles.wineDetails}>
                            {bid.producer} {bid.region && `• ${bid.region}`} {bid.vintage ? `• ${bid.vintage}` : ''}
                            {bid.bottleVolume && ` • ${bid.bottleVolume}`}
                            {caseSize && ` × ${caseSize}`}
                            {bid.casesQty > 1 && ` (${bid.casesQty} cases)`}
                        </div>
                        <div style={styles.pillRow}>
                            {bid.bidStatus && (
                                <span style={styles.pill('#ede9fe', '#5b21b6')}>{bid.bidStatus}</span>
                            )}
                        </div>
                        <div style={styles.priceRow}>
                            <span><span style={styles.priceLabel}>Bid Price: </span><span style={styles.priceValue}>{formatPrice(bid.proposedPrice)}</span></span>
                            {marketPrice != null && (
                                <span><span style={styles.priceLabel}>Market: </span><span style={styles.priceValue}>{formatPrice(marketPrice)}</span></span>
                            )}
                            {wsPrice != null && (
                                <span><span style={styles.priceLabel}>WS Low: </span><span style={styles.priceValue}>{formatPrice(wsPrice)}</span></span>
                            )}
                            {bid.createdAt && (
                                <span style={styles.priceLabel}>Placed: {new Date(bid.createdAt).toLocaleDateString('en-GB')}</span>
                            )}
                        </div>
                        {compareChips.length > 0 && (
                            <div style={styles.pillRow}>{compareChips}</div>
                        )}
                    </div>
                    <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-end' }}>
                        {masterRef ? (
                            <>
                                <a href={`/master-products?vintageId=${encodeURIComponent(masterRef.masterProductVintageId)}`} style={styles.matchLink}>
                                    <Link2 size={12} /> {masterRef.masterProductName} ({masterRef.vintage})
                                </a>
                                {masterRef.sourceUrl && (
                                    <a href={masterRef.sourceUrl} target="_blank" rel="noopener noreferrer" style={styles.matchLink}>
                                        <ExternalLink size={12} /> View on BBR
                                    </a>
                                )}
                                {masterSkuRef && (
                                    <span style={{ fontSize: '11px', color: 'var(--ink-500)', fontFamily: 'monospace' }}>
                                        SKU: {masterSkuRef.canonicalSkuCode}
                                    </span>
                                )}
                                {!masterSkuRef && (
                                    <span style={{ fontSize: '11px', color: 'var(--warning)' }}>
                                        Vintage match only
                                    </span>
                                )}
                            </>
                        ) : (
                            <span style={styles.unmatchedLabel}>
                                <AlertTriangle size={12} /> Unmatched
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {/* Header */}
            <div style={styles.header}>
                <h1 style={styles.title}>
                    <Wine size={28} />
                    My Cellar
                </h1>
                <div style={styles.headerRight}>
                    {authStatus && (
                        <span style={styles.authInfo}>
                            <span style={styles.authDot(authStatus.sessionActive)}></span>
                            {authStatus.sessionActive ? `Key: ${formatExpiry(authStatus.keyExpiry)}` : 'Not connected'}
                        </span>
                    )}
                    <button style={styles.refreshButton} onClick={handleRefresh} disabled={refreshing}>
                        <RefreshCw size={16} className={refreshing ? 'spinner' : ''} />
                        {refreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
            </div>

            {/* Summary Dashboard */}
            {summary && (
                <div style={styles.summaryGrid}>
                    <div style={styles.statCard('var(--surface)', 'var(--ink-900)')}>
                        <div style={styles.statValue('var(--ink-900)')}>{summary.totalCellarItems}</div>
                        <div style={styles.statLabel}>Total Cellar Items</div>
                    </div>
                    <div style={styles.statCard('var(--success-bg)', 'var(--success)')}>
                        <div style={styles.statValue('var(--success)')}>{summary.listedOnBbx}</div>
                        <div style={styles.statLabel}>BBX Listings</div>
                    </div>
                    <div style={styles.statCard('var(--warning-bg)', '#854d0e')}>
                        <div style={styles.statValue('#854d0e')}>{summary.withBidsReceived}</div>
                        <div style={styles.statLabel}>Bids Received</div>
                    </div>
                    <div style={styles.statCard('#faf5ff', '#5b21b6')}>
                        <div style={styles.statValue('#5b21b6')}>{summary.bidsPlaced}</div>
                        <div style={styles.statLabel}>Bids Placed</div>
                    </div>
                    <div style={styles.statCard('var(--info-bg)', 'var(--info)')}>
                        <div style={styles.statValue('var(--info)')}>{summary.matchedToMaster}</div>
                        <div style={styles.statLabel}>Matched to Master</div>
                    </div>
                    <div style={styles.statCard('var(--warning-bg)', '#c2410c')}>
                        <div style={styles.statValue('#c2410c')}>{summary.unmatchedItems}</div>
                        <div style={styles.statLabel}>Unmatched</div>
                    </div>
                </div>
            )}

            {/* Tab Bar */}
            <div style={styles.tabBar}>
                {(Object.keys(tabData) as TabKey[]).map((key) => (
                    <button
                        key={key}
                        style={styles.tab(activeTab === key)}
                        onClick={() => setActiveTab(key)}
                    >
                        {tabData[key].label} ({tabData[key].count})
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {activeTab === 'all' && (
                cellar.length === 0
                    ? <div style={{ textAlign: 'center', padding: '48px', color: 'var(--ink-500)' }}>No cellar items found</div>
                    : cellar.map(renderCellarCard)
            )}

            {activeTab === 'bbx' && (
                bbxListings.length === 0
                    ? <div style={{ textAlign: 'center', padding: '48px', color: 'var(--ink-500)' }}>No BBX listings found</div>
                    : bbxListings.map(renderCellarCard)
            )}

            {activeTab === 'bidsReceived' && (
                bidsReceived.length === 0
                    ? <div style={{ textAlign: 'center', padding: '48px', color: 'var(--ink-500)' }}>No bids received</div>
                    : bidsReceived.map(renderCellarCard)
            )}

            {activeTab === 'bidsPlaced' && (
                bidsPlaced.length === 0
                    ? <div style={{ textAlign: 'center', padding: '48px', color: 'var(--ink-500)' }}>No bids placed</div>
                    : bidsPlaced.map(renderBidCard)
            )}
        </div>
    );
};

export default BbrAccount;
